import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { userService } from '../services/userService';
import walletService from '../services/walletService';
//  SECURITY: Import device fingerprint service for login tracking

import { deviceFingerprintService } from '../services/security';

export interface UserProfile {
  uid: string;
  email: string;
  username: string;
  fullName?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  bio?: string;
  photoURL?: string;
  showFullName?: boolean;
  referralCode: string;
  referrerId?: string;
  kycStatus: 'pending' | 'verified' | 'rejected';
  createdAt: Date;
  playCoins: number;
  pkCash: number;
  followersCount?: number;
  followingCount?: number;
  //  BAN SYSTEM FIELDS

  banned?: boolean;
  banReason?: string;
  banType?: 'temporary' | 'permanent';
  banExpiresAt?: Date;
  bannedAt?: Date;
  // Chat moderation (server-set): muted until this time after repeated hate speech
  chatMutedUntil?: Date;
  chatStrikes?: number;
  stats?: {
    gamesPlayed: number;
    totalWinnings: number;
    handsWon: number;
    level: number;
  };
  personalLimits?: {
    dailyDepositLimit?: number;
    weeklyDepositLimit?: number;
    monthlyDepositLimit?: number;
    sessionTimeLimit?: number;
    lossLimit?: number;
    enabled: boolean;
  };
}

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: UserProfile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  updateProfilePhoto: (photoURL: string | null) => Promise<void>;
  updatePersonalInfo: (info: Partial<UserProfile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  userProfile: null,
  loading: true,
  refreshProfile: async () => {},
  updateProfilePhoto: async () => {},
  updatePersonalInfo: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUserProfile = useCallback(async (uid: string, forceRefresh = false) => {
    try {
      // Invalidate cache if force refresh
      if (forceRefresh) {
        userService.invalidateCache(uid);
      }

      const [profile, balances] = await Promise.all([
        userService.getUserProfile(uid, forceRefresh),
        walletService.getBothBalances(uid),
      ]);

      if (profile) {
        setUserProfile({
          ...profile,
          playCoins: balances.playCoins,
          pkCash: balances.pkCash,
          createdAt: profile.createdAt?.toDate ? profile.createdAt.toDate() : new Date(),
          //  BAN FIELDS

          banned: profile.banned || false,
          banReason: profile.banReason,
          banType: profile.banType,
          banExpiresAt: profile.banExpiresAt?.toDate ? profile.banExpiresAt.toDate() : undefined,
          bannedAt: profile.bannedAt?.toDate ? profile.bannedAt.toDate() : undefined,
        } as UserProfile);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (currentUser?.uid) {
      await loadUserProfile(currentUser.uid, true);
    }
  }, [currentUser?.uid, loadUserProfile]);

  const updateProfilePhoto = useCallback(async (photoURL: string | null) => {
    if (!currentUser?.uid) return;
    
    await userService.updateProfilePhoto(currentUser.uid, photoURL);
    
    // Update local state immediately
    setUserProfile(prev => prev ? { ...prev, photoURL: photoURL || undefined } : null);
  }, [currentUser?.uid]);

  const updatePersonalInfo = useCallback(async (info: Partial<UserProfile>) => {
    if (!currentUser?.uid) return;
    
    await userService.updatePersonalInfo(currentUser.uid, info);
    
    // Update local state immediately
    setUserProfile(prev => prev ? { ...prev, ...info } : null);
  }, [currentUser?.uid]);

  useEffect(() => {
    let unsubscribeProfile: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        await loadUserProfile(user.uid);
        
        //  REAL-TIME BAN LISTENER - Kicks user immediately when banned

        unsubscribeProfile = onSnapshot(
          doc(db, 'users', user.uid),
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              setUserProfile(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  banned: data.banned || false,
                  banReason: data.banReason,
                  banType: data.banType,
                  banExpiresAt: data.banExpiresAt?.toDate ? data.banExpiresAt.toDate() : undefined,
                  bannedAt: data.bannedAt?.toDate ? data.bannedAt.toDate() : undefined,
                  chatMutedUntil: data.chatMutedUntil?.toDate ? data.chatMutedUntil.toDate() : undefined,
                  chatStrikes: data.chatStrikes || 0,
                };
              });
            }
          },
          (error) => {
            console.warn('⚠️ Ban listener error (non-blocking):', error);
          }
        );
        
        //  SECURITY: Capture device fingerprint and IP on login (non-blocking)

        deviceFingerprintService.captureSecurityContext(user.uid, 'login').catch(err => {
          console.warn('⚠️ Security context capture failed (non-blocking):', err);
        });
      } else {
        setUserProfile(null);
        if (unsubscribeProfile) {
          unsubscribeProfile();
          unsubscribeProfile = null;
        }
      }
      
      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, [loadUserProfile]);

  return (
    <AuthContext.Provider 
      value={{ 
        currentUser, 
        userProfile, 
        loading, 
        refreshProfile,
        updateProfilePhoto,
        updatePersonalInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};